import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getUserRoles, getUserSalons, checkUserPermission, isSuperAdmin } from '../firebase/userRoles';

// Criação do contexto
const UserContext = createContext(null);

// Hook personalizado para usar o contexto
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUser deve ser usado dentro de um UserProvider');
  }
  return context;
};

// Provedor do contexto
export const UserProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [userRoles, setUserRoles] = useState(null);
  const [userSalons, setUserSalons] = useState([]);
  const [selectedSalonId, setSelectedSalonId] = useState(null);
  const [isSuperAdminUser, setIsSuperAdminUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState(0);
  
  // Carregar roles e verificar se é super admin
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) {
        setUserRoles(null);
        setUserSalons([]);
        setSelectedSalonId(null);
        setIsSuperAdminUser(false);
        setLoading(false);
        setIsInitialized(true);
        return;
      }
      
      try {
        setLoading(true);
        
        // Verificar se é super admin
        const superAdmin = await isSuperAdmin(currentUser.uid);
        setIsSuperAdminUser(superAdmin);
        
        // Carregar roles
        const roles = await getUserRoles(currentUser.uid);
        setUserRoles(roles);
        
        // Carregar salões
        const salons = await getUserSalons(currentUser.uid);
        setUserSalons(salons);
        
        // Recuperar salão selecionado do localStorage, se existir
        const storedSalonId = localStorage.getItem('selectedSalonId');
        const validSalonId = storedSalonId && salons.some(salon => salon.id === storedSalonId)
          ? storedSalonId
          : (salons.length > 0 ? salons[0].id : null);
        
        if (validSalonId) {
          setSelectedSalonId(validSalonId);
          localStorage.setItem('selectedSalonId', validSalonId);
        }
        
        // Marca timestamp da última atualização
        setLastRefreshTimestamp(Date.now());
      } catch (error) {
        console.error('[UserContext] Erro ao carregar dados do usuário:', error);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    };
    
    loadUserData();
  }, [currentUser]);
  
  // Verificar permissão no salão atual
  const hasPermission = useCallback(async (permission, salonId = null) => {
    if (!currentUser) return false;
    const idToUse = salonId || selectedSalonId;
    if (!idToUse) return false;
    return checkUserPermission(currentUser.uid, idToUse, permission);
  }, [currentUser, selectedSalonId]);
  
  // Trocar o salão selecionado
  const switchSalon = useCallback((salonId) => {
    if (userSalons.some(salon => salon.id === salonId)) {
      setSelectedSalonId(salonId);
      localStorage.setItem('selectedSalonId', salonId);
      return true;
    }
    return false;
  }, [userSalons]);
  
  // Obter informações do salão atual
  const getCurrentSalon = useCallback((salonId = null) => {
    const idToUse = salonId || selectedSalonId;
    return userSalons.find(salon => salon.id === idToUse) || null;
  }, [userSalons, selectedSalonId]);
  
  // Obter o papel do usuário no salão atual
  const getCurrentRole = useCallback((salonId = null) => {
    const salon = getCurrentSalon(salonId);
    return salon ? salon.role : null;
  }, [getCurrentSalon]);
  
  // Recarregar dados do usuário (útil após mudanças)
  const refreshUserData = useCallback(async (forceRefresh = false) => {
    // Prevenir múltiplas atualizações num curto período
    const now = Date.now();
    const timeSinceLastRefresh = now - lastRefreshTimestamp;
    
    if (!forceRefresh && timeSinceLastRefresh < 2000) {
      return {
        isSuperAdmin: isSuperAdminUser,
        userRoles,
        userSalons,
        selectedSalonId
      };
    }
    
    if (!currentUser) {
      return null;
    }
    
    try {
      setLoading(true);
      
      // Verificar se é super admin
      const superAdmin = await isSuperAdmin(currentUser.uid);
      setIsSuperAdminUser(superAdmin);
      
      // Carregar roles
      const roles = await getUserRoles(currentUser.uid);
      setUserRoles(roles);
      
      // Carregar salões
      const salons = await getUserSalons(currentUser.uid);
      setUserSalons(salons);
      
      // Verificar se o salão atual ainda está acessível
      if (selectedSalonId && !salons.some(salon => salon.id === selectedSalonId)) {
        const newSalonId = salons.length > 0 ? salons[0].id : null;
        
        setSelectedSalonId(newSalonId);
        if (newSalonId) {
          localStorage.setItem('selectedSalonId', newSalonId);
        } else {
          localStorage.removeItem('selectedSalonId');
        }
      } else if (!selectedSalonId && salons.length > 0) {
        // Se não tiver salão selecionado mas tiver salões disponíveis, selecionar o primeiro
        const newSalonId = salons[0].id;
        setSelectedSalonId(newSalonId);
        localStorage.setItem('selectedSalonId', newSalonId);
      }
      
      // Atualizar timestamp
      setLastRefreshTimestamp(Date.now());
      
      // Retornar os dados atualizados para uso pelo chamador
      const result = {
        isSuperAdmin: superAdmin,
        userRoles: roles,
        userSalons: salons,
        selectedSalonId: selectedSalonId || (salons.length > 0 ? salons[0].id : null)
      };
      
      return result;
    } catch (error) {
      console.error('[UserContext] Erro ao atualizar dados do usuário:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentUser, selectedSalonId, lastRefreshTimestamp, isSuperAdminUser, userRoles, userSalons]);
  
  // Objeto com valores e funções a serem disponibilizadas pelo contexto
  const value = {
    userRoles,
    userSalons,
    loading,
    selectedSalonId,
    isSuperAdmin: isSuperAdminUser,
    hasPermission,
    switchSalon,
    getCurrentSalon,
    getCurrentRole,
    refreshUserData,
    isInitialized
  };
  
  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext; 